/**
 * Guest Oblíbené — localStorage favourites + soft sign-in banner.
 * Platform route `/favorites` — guest localStorage favourites or signed-in
 * AccountShopContactsMachine (no redirect; auto-select catalog is suppressed here).
 *
 * Display SoT = unscoped public customer-sales-points (same Obchody gate as `/shops`).
 * Fail closed when the directory allowlist is pending or unavailable.
 * Chrome parity with Shops: DirectoryPageHeader → List/Map → DirectoryFindToolbar.
 */

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AccountShopContactsMachine } from './AccountShopContactsMachine.js';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  toSameOriginCatalogImageUrl,
  classifyCatalogImageUrl,
  resolveLocalizedName,
  normalizeLogoChipBackgroundSettings,
  normalizeLogoChipRimSettings,
} from 'pi-kiosk-shared';
import {
  BOTTOM_NAV_AUTH_INTENT_STATE_KEY,
  setBottomNavMemoryAuthIntent,
} from '../../app/bottomNavActiveTab.js';
import { CUSTOMER_CONTENT_FULL_CLASS } from '../../app/customerTopBarStyles.js';
import { useTheme } from '../../app/ThemeProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { invalidatePublicCustomerSalesPointsCache } from '../../shared/api/customerDirectoryCacheInvalidate.js';
import {
  PLATFORM_DIRECTORY_SHOPS_TENANT,
  readDirectoryFavoritesPersist,
  writeDirectoryFavoritesPersist,
} from '../../shared/session/searchPersistStorage.js';
import { isAccountAccessUsable } from './accountApi.js';
import {
  DIRECTORY_UI_PREFS_HYDRATED_EVENT,
  putDirectoryFavoritesSortRemote,
} from './useDirectoryUiPreferenceSync.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import { PLATFORM_CHROME_TENANT_CODE } from '../../app/platformChromeTenant.js';
import { parseDirectoryViewMode } from '../../shared/session/directoryViewMode.js';
import { savePlatformReturnTarget } from '../../shared/session/returnTarget.js';
import { parkMapViewSignInReturnTarget } from './mapViewSignInGate.js';
import { Card } from '../../shared/ui/Card.js';
import { Button } from '../../shared/ui/Button.js';
import { FavoriteStarIcon } from '../../shared/ui/FavoriteStarIcon.js';
import { ConfirmDialog } from '../../shared/ui/ConfirmDialog.js';
import {
  DirectoryEntityRow,
  type DirectoryEntityRowLayout,
} from '../../shared/ui/DirectoryEntityRow.js';
import { DirectoryFindToolbar } from '../../shared/ui/DirectoryFindToolbar.js';
import { DirectoryListMapViewSwitch } from '../../shared/ui/DirectoryListMapViewSwitch.js';
import { DirectoryPageHeader } from '../../shared/ui/DirectoryPageHeader.js';
import {
  DirectoryResults,
  DirectoryResultsSkeleton,
} from '../../shared/ui/DirectoryResults.js';
import {
  buildOrgFacets,
  filterDirectoryShops,
  type DirectorySortId,
} from '../../shared/ui/directoryListFilter.js';
import {
  readDirectoryGeoAllowedFlag,
  writeDirectoryGeoAllowedFlag,
} from '../../shared/ui/directoryGeoAllowed.js';
import {
  decideDirectoryGeoWarm,
  type DirectoryGeoPermissionProbe,
} from '../../shared/ui/directoryGeoWarmDecision.js';
import {
  DIRECTORY_DENSITY_STORAGE_KEYS,
  useDirectoryDensity,
  type DirectoryDensityMode,
} from '../../shared/ui/directoryDensity.js';
import {
  DIRECTORY_CARD_ITEM_CLASS,
  DIRECTORY_CARD_PAD_CLASS,
  DIRECTORY_FAVORITES_LIST_ITEM_CLASS,
  DIRECTORY_EMPTY_PANEL_FULL_CLASS,
  resolveEmptyPanelClass,
  DIRECTORY_PAGE_STACK_CLASS,
} from '../../shared/ui/directoryPickerLayout.js';
import { useShortLandscapeViewport } from '../../shared/hooks/useShortLandscapeViewport.js';
import { useStoreDirectoryThumb } from '../../shared/ui/useDirectoryEntityThumb.js';
import { showToast } from '../../shared/ui/toastStore.js';
import { useCustomerFeatureFlags } from '../flags/useCustomerFeatureFlags.js';
import { isCustomerBrowseEnabled } from '../../config/customerFeatureFlags.js';
import {
  PLATFORM_BROWSE_PATH,
  PLATFORM_FAVORITES_PATH,
  PLATFORM_ORGS_PATH,
  PLATFORM_SHOPS_PATH,
} from '../shop/resolveShopHref.js';
import { PLATFORM_SIGN_UP_PATH } from '../auth/customerAuthPaths.js';
import {
  fetchPublicCustomerSalesPointListableAllowlist,
  fetchPublicCustomerSalesPoints,
  type PublicCustomerSalesPointDTO,
} from '../shops/publicSalesPointsApi.js';
import { ShopsDirectoryMap } from '../shops/ShopsDirectoryMap.js';
import { useShopsDirectoryMapViews } from '../shops/useShopsDirectoryMapViews.js';
import { useDirectoryViewModeSearch } from '../shops/useDirectoryViewModeSearch.js';
import {
  shopsDirectoryQueryOptions,
  useShopsDirectoryRealtimeSync,
} from '../shops/hooks/useShopsDirectoryRealtimeSync.js';
import { resolveDirectoryShopOpenAction } from '../shops/resolveDirectoryShopOpenAction.js';
import { resolveShopLocationLabelWithT } from '../shops/resolveShopLocationLabel.js';
import {
  filterPendingFavoritesByListableDirectory,
  isPendingFavoriteListableOnDirectory,
} from './customerRetailListable.js';
import {
  type PendingFavoriteSpContact,
  getPendingFavoriteSpContactsServerSnapshot,
  getPendingFavoriteSpContactsSnapshot,
  mergePendingFavoriteBrandingFromLive,
  readPendingFavoriteSpContacts,
  removePendingFavoriteSpContact,
  subscribePendingFavoriteSpContacts,
} from './pendingFavoriteSpContacts.js';

/** Session-only dismiss for the guest sign-in banner (tab/window lifetime). */
export const GUEST_FAVORITES_SIGNIN_BANNER_DISMISSED_KEY =
  'customer:guestFavoritesSignInBannerDismissed';

const FAVORITES_DIRECTORY_SORT_OPTIONS = [
  'nameAsc',
  'nameDesc',
  'orgAsc',
  'distanceAsc',
] as const satisfies readonly DirectorySortId[];

interface UserPosition {
  readonly lat: number;
  readonly lng: number;
}

const GEO_TIMEOUT_MS = 10_000;

function acquireBrowserPosition(
  onSuccess: (position: UserPosition) => void,
  onError: (denied: boolean) => void,
): () => void {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.geolocation?.getCurrentPosition !== 'function'
  ) {
    onError(false);
    return () => undefined;
  }
  let cancelled = false;
  navigator.geolocation.getCurrentPosition(
    (position) => {
      if (cancelled) {
        return;
      }
      onSuccess({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
    },
    (error) => {
      if (cancelled) {
        return;
      }
      onError(error.code === error.PERMISSION_DENIED);
    },
    { timeout: GEO_TIMEOUT_MS, enableHighAccuracy: false },
  );
  return () => {
    cancelled = true;
  };
}

function readGuestFavoritesSignInBannerDismissed(): boolean {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    return false;
  }
  try {
    return window.sessionStorage.getItem(GUEST_FAVORITES_SIGNIN_BANNER_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeGuestFavoritesSignInBannerDismissed(): void {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(GUEST_FAVORITES_SIGNIN_BANNER_DISMISSED_KEY, '1');
  } catch {
    // sessionStorage may be unavailable (private mode quota) — hide in-memory only.
  }
}

function favoritesResultItemClass(density: DirectoryDensityMode): string {
  return density === 'cards'
    ? [DIRECTORY_CARD_ITEM_CLASS, DIRECTORY_CARD_PAD_CLASS].join(' ')
    : DIRECTORY_FAVORITES_LIST_ITEM_CLASS;
}

type ListableDirectoryAllowlist = {
  readonly allowedIds: Set<number>;
  readonly allowedSlugKeys: Set<string>;
};

function resolveGuestFavoriteRowPressHref(item: PendingFavoriteSpContact): string | undefined {
  const shopUrl = typeof item.customerShopUrl === 'string' ? item.customerShopUrl : '';
  if (shopUrl.length === 0) {
    return undefined;
  }
  const action = resolveDirectoryShopOpenAction({
    customerShopUrl: shopUrl,
    targetTenantCode: item.tenantCode,
  });
  return action?.url;
}

function isGuestFavoriteRowPressEnabled(
  item: PendingFavoriteSpContact,
  allowlistReady: boolean,
  allowlistData: ListableDirectoryAllowlist | undefined,
): boolean {
  const shopUrl = typeof item.customerShopUrl === 'string' ? item.customerShopUrl : '';
  if (shopUrl.length === 0) {
    return false;
  }
  if (!allowlistReady || allowlistData === undefined) {
    return false;
  }
  return isPendingFavoriteListableOnDirectory(
    item,
    allowlistData.allowedIds,
    allowlistData.allowedSlugKeys,
  );
}

/**
 * Soft guest CTA — width tracks density of the surface above it
 * (empty → full column; list → Seznam cell; cards → dense Karty lg 3-col cell).
 * Primary/secondary CTAs share one content width: longest label + Button lg
 * horizontal pad (~20px / px-5 each side); equal on every viewport.
 */
function GuestFavoritesSignInBanner({
  onHide,
  density = 'empty',
}: {
  readonly onHide: () => void;
  /** empty → full column; list → Seznam cell; cards → dense Karty lg 3-col cell. */
  readonly density?: DirectoryDensityMode | 'empty';
}): JSX.Element {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * G4 — any visible banner while `?view=map` (empty, reconciling, or list
   * mis-render) must elevate like Views CTA: parkMapResume via the gate,
   * fail-closed (no navigate when park is null). List-only stays generic.
   */
  const rememberFavoritesReturn = (): boolean => {
    if (parseDirectoryViewMode(location.search) === 'map') {
      const parked = parkMapViewSignInReturnTarget(
        `${location.pathname}${location.search}`,
        PLATFORM_CHROME_TENANT_CODE,
      );
      if (parked === null) {
        return false;
      }
      setBottomNavMemoryAuthIntent('shop');
      return true;
    }
    savePlatformReturnTarget(PLATFORM_FAVORITES_PATH);
    setBottomNavMemoryAuthIntent('shop');
    return true;
  };

  const goCreateAccount = (): void => {
    if (!rememberFavoritesReturn()) {
      return;
    }
    // Platform `/sign-up` is the registration form (org select on the form).
    navigate(PLATFORM_SIGN_UP_PATH, {
      state: { [BOTTOM_NAV_AUTH_INTENT_STATE_KEY]: 'shop' },
    });
  };

  const goSignIn = (): void => {
    if (!rememberFavoritesReturn()) {
      return;
    }
    navigate('/sign-in', {
      state: { [BOTTOM_NAV_AUTH_INTENT_STATE_KEY]: 'shop' },
    });
  };

  return (
    <Card
      padded
      className={[
        resolveEmptyPanelClass(density),
        'border border-[var(--color-border)]',
      ].join(' ')}
      data-testid="guest-favorites-signin-banner"
    >
      <div className="flex items-start gap-2">
        <p className="min-w-0 flex-1 text-sm text-[var(--color-on-surface)]">
          {t('account.shop.guestFavoritesBrowserOnly')}
        </p>
        <button
          type="button"
          className={[
            'inline-flex h-11 w-11 min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full',
            'text-lg leading-none text-[var(--color-on-surface-muted)]',
            'hover:bg-[var(--color-surface-muted)]',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
            'focus-visible:outline-[var(--color-focus-ring)]',
          ].join(' ')}
          aria-label={t('account.shop.guestFavoritesHideBannerAria')}
          title={t('account.shop.guestFavoritesHideBanner')}
          data-testid="guest-favorites-signin-hide"
          onClick={onHide}
        >
          ×
        </button>
      </div>
      <div
        className="mt-3 flex w-full flex-col items-center gap-3"
        data-testid="guest-favorites-signin-actions"
      >
        {/*
          w-max + single-column grid → both CTAs equal the longer label’s
          intrinsic width (Button lg already px-5 ≈ 20px each side).
          max-w-full keeps them inside the banner on narrow panes.
        */}
        <div
          className="inline-grid max-w-full gap-3"
          data-testid="guest-favorites-signin-cta-pair"
        >
          <Button
            type="button"
            intent="primary"
            size="lg"
            className="w-full whitespace-nowrap"
            data-testid="guest-favorites-signin-cta"
            onClick={goCreateAccount}
          >
            {t('account.shop.guestFavoritesSignIn')}
          </Button>
          <Button
            type="button"
            intent="secondary"
            size="lg"
            className="w-full whitespace-nowrap"
            data-testid="guest-favorites-login-cta"
            onClick={goSignIn}
          >
            {t('account.shop.guestFavoritesLogIn')}
          </Button>
        </div>
        <Link
          className={[
            'inline-flex min-h-11 w-full max-w-full items-center justify-center rounded-md px-3 text-center text-sm',
            'text-[var(--color-accent)] underline',
          ].join(' ')}
          to={PLATFORM_SHOPS_PATH}
          data-testid="guest-favorites-browse-shops"
        >
          {t('account.shop.browsePublicShops')}
        </Link>
      </div>
    </Card>
  );
}

function GuestFavoriteRow({
  item,
  allowlistReady,
  allowlistData,
  onRequestUnfavorite,
  layout = 'inline',
}: {
  readonly item: PendingFavoriteSpContact;
  readonly allowlistReady: boolean;
  readonly allowlistData: ListableDirectoryAllowlist | undefined;
  readonly onRequestUnfavorite: () => void;
  readonly layout?: DirectoryEntityRowLayout;
}): JSX.Element {
  const { t, i18n } = useTranslation('customer');
  const { effectiveTheme } = useTheme();
  const { apiBaseUrl } = useRuntimeConfig();
  const queryClient = useQueryClient();
  const displayName = resolveLocalizedName(item.name, item.nameLocales, i18n.language);
  const imageUrl = toSameOriginCatalogImageUrl(item.imageUrl, {
    tenantCode: item.tenantCode,
  });
  const imageUrlDark = toSameOriginCatalogImageUrl(item.imageUrlDark, {
    tenantCode: item.tenantCode,
  });
  const tenantLogoUrl = toSameOriginCatalogImageUrl(item.tenantLogoUrl, {
    tenantCode: item.tenantCode,
  });
  const tenantLogoUrlDark = toSameOriginCatalogImageUrl(item.tenantLogoUrlDark, {
    tenantCode: item.tenantCode,
  });
  const salesPointId = item.salesPointId ?? 0;
  const tenantLabel = typeof item.tenantName === 'string' ? item.tenantName.trim() : '';
  // A13b — ONLINE_ONLY → Shipping only (never locationMissing for null ops address).
  const locationLabel = resolveShopLocationLabelWithT(
    {
      customerPresence: item.customerPresence,
      location: item.location,
      locationMissingKey: 'account.shop.locationMissing',
    },
    t,
  );
  const subtitle =
    [tenantLabel.length > 0 ? tenantLabel : null, locationLabel]
      .filter((part): part is string => part !== null && part.length > 0)
      .join(' · ');

  const onMediaFailed = useCallback(
    (failedSrc: string): void => {
      if (classifyCatalogImageUrl(failedSrc) === 'signed_api') {
        invalidatePublicCustomerSalesPointsCache(queryClient, apiBaseUrl);
      }
    },
    [apiBaseUrl, queryClient],
  );

  const { thumbProps } = useStoreDirectoryThumb({
    imageUrl,
    imageUrlDark,
    tenantLogoUrl,
    tenantLogoUrlDark,
    monogram: {
      id: salesPointId,
      displayName,
      code: item.code,
    },
    showTenantChip: false,
    size: 'lg',
    logoChipRim: normalizeLogoChipRimSettings(item),
    logoChipBackground: normalizeLogoChipBackgroundSettings(item),
    logoChipRimTheme: effectiveTheme,
    onMediaFailed,
    testId: `customer-guest-favorites-thumb-${salesPointId}`,
  });

  const rowPressHref = resolveGuestFavoriteRowPressHref(item);
  const rowPressEnabled = isGuestFavoriteRowPressEnabled(item, allowlistReady, allowlistData);

  return (
    <DirectoryEntityRow
      thumb={thumbProps}
      title={displayName}
      subtitle={subtitle}
      rowPressHref={rowPressHref}
      rowPressDisabled={!rowPressEnabled || rowPressHref === undefined}
      rowPressTestId={`customer-guest-favorites-open-${salesPointId}`}
      showChevron
      density="comfortable"
      layout={layout}
      testId={`customer-guest-favorites-row-${salesPointId}`}
      trailing={
        <button
          type="button"
          className={[
            'inline-flex h-11 w-11 min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full border border-transparent bg-transparent',
            'text-[var(--color-warning)] hover:bg-[var(--color-surface-muted)]',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
            'disabled:cursor-not-allowed disabled:opacity-[0.55]',
          ].join(' ')}
          data-testid={`customer-guest-favorites-unstar-${salesPointId}`}
          title={t('account.shop.unfavorite')}
          aria-label={t('account.shop.unfavorite')}
          onClick={onRequestUnfavorite}
        >
          <FavoriteStarIcon filled />
        </button>
      }
    />
  );
}

type FavoriteFilterItem = PendingFavoriteSpContact & {
  readonly lat: number | null;
  readonly lng: number | null;
};

export function GuestFavoritesScreen(): JSX.Element {
  const { t, i18n } = useTranslation('customer');
  const { apiBaseUrl } = useRuntimeConfig();
  const { session, sessionHydrated } = useCustomerSession();
  const {
    density,
    setDensity,
    showToggle: showDensityToggle,
  } = useDirectoryDensity(DIRECTORY_DENSITY_STORAGE_KEYS.favorites);
  // G13 / G7 — directory SSE + consume isConnected for 30s poll when SSE down.
  const directorySseConnected = useShopsDirectoryRealtimeSync();
  const shortLandscape = useShortLandscapeViewport();
  // G5 Spec A / G4: signed-in chrome = session after `/me` only.
  // BAN getToken / jwt LS sole proof — empty-access `/me` 200 still redirects when home known.
  const signedIn = isGloballySignedInChrome({ session });
  const awaitingCredentialsHydrate = !sessionHydrated && signedIn === false;
  const [signInBannerDismissed, setSignInBannerDismissed] = useState(
    readGuestFavoritesSignInBannerDismissed,
  );
  const hideSignInBanner = useCallback((): void => {
    writeGuestFavoritesSignInBannerDismissed();
    setSignInBannerDismissed(true);
  }, []);
  const showSignInBanner = !signedIn && !signInBannerDismissed;
  const { flags } = useCustomerFeatureFlags();
  const showBrowseNav = isCustomerBrowseEnabled(flags);
  const storageSnap = useSyncExternalStore(
    subscribePendingFavoriteSpContacts,
    getPendingFavoriteSpContactsSnapshot,
    getPendingFavoriteSpContactsServerSnapshot,
  );
  const localItems = useMemo(() => {
    void storageSnap;
    return readPendingFavoriteSpContacts();
  }, [storageSnap]);

  const [query, setQuery] = useState(
    () => readDirectoryFavoritesPersist()?.q ?? '',
  );
  const [sort, setSort] = useState<DirectorySortId>(
    () => readDirectoryFavoritesPersist()?.sort ?? 'nameAsc',
  );
  const [orgCode, setOrgCode] = useState<string | null>(
    () => readDirectoryFavoritesPersist()?.orgCode ?? null,
  );
  const [userPosition, setUserPosition] = useState<UserPosition | null>(null);
  const [geoPending, setGeoPending] = useState(
    () => (readDirectoryFavoritesPersist()?.sort ?? 'nameAsc') === 'distanceAsc',
  );
  const { viewMode, setViewMode } = useDirectoryViewModeSearch();
  const [selectedSalesPointId, setSelectedSalesPointId] = useState<number | null>(null);
  // Hook gates fetch on signed-in chrome; guest sees empty savedViews (settled, not loading).
  const {
    savedViews,
    savedViewsLoading,
    savedViewsError,
    onRetrySavedViews,
    deletePending,
    renamePending,
    onRequestDeleteView,
    onRequestRenameView,
  } = useShopsDirectoryMapViews();

  useEffect(() => {
    writeDirectoryFavoritesPersist(
      {
        q: query,
        sort,
        orgCode,
      },
      {
        hasUserPosition: userPosition !== null,
        geoPending: sort === 'distanceAsc' && geoPending,
      },
    );
  }, [geoPending, orgCode, query, sort, userPosition]);

  const handleSortChange = useCallback(
    (next: DirectorySortId): void => {
      if (next === 'distanceAsc') {
        if (userPosition === null) {
          setGeoPending(true);
        }
      } else {
        setGeoPending(false);
      }
      setSort(next);
      if (isAccountAccessUsable(session)) {
        putDirectoryFavoritesSortRemote(apiBaseUrl, next);
      }
    },
    [apiBaseUrl, session, userPosition],
  );

  useEffect(() => {
    const onHydrated = (): void => {
      const restored = readDirectoryFavoritesPersist();
      // G1 — never skip when persist null; apply nameAsc default.
      if (restored === null) {
        setSort('nameAsc');
        setQuery('');
        setOrgCode(null);
        setGeoPending(false);
        return;
      }
      setSort(restored.sort);
      setQuery(restored.q);
      setOrgCode(restored.orgCode);
      setGeoPending(restored.sort === 'distanceAsc');
    };
    window.addEventListener(DIRECTORY_UI_PREFS_HYDRATED_EVENT, onHydrated);
    return () => {
      window.removeEventListener(DIRECTORY_UI_PREFS_HYDRATED_EVENT, onHydrated);
    };
  }, []);

  // Nearest sort (or restored distanceAsc) — acquire position; keep it across other sorts.
  useEffect(() => {
    if (sort !== 'distanceAsc' || userPosition !== null) {
      return;
    }
    let sortResetHandle: number | undefined;
    const release = acquireBrowserPosition(
      (position) => {
        writeDirectoryGeoAllowedFlag(true);
        setUserPosition(position);
        setGeoPending(false);
      },
      (denied) => {
        if (denied) {
          writeDirectoryGeoAllowedFlag(false);
        }
        showToast({
          message: t(
            denied
              ? 'directory.filters.locationDenied'
              : 'directory.filters.locationUnavailable',
          ),
          variant: 'warn',
        });
        setUserPosition(null);
        setGeoPending(false);
        // G3 — persist + PUT synchronously so unmount clearTimeout cannot cancel remote demotion.
        writeDirectoryFavoritesPersist(
          {
            q: query,
            sort: 'nameAsc',
            orgCode,
          },
          {
            hasUserPosition: false,
            geoPending: false,
          },
        );
        if (isAccountAccessUsable(session)) {
          putDirectoryFavoritesSortRemote(apiBaseUrl, 'nameAsc');
        }
        // Defer only React sort state — avoids set-state-in-effect sync cascade.
        sortResetHandle = window.setTimeout(() => {
          setSort('nameAsc');
          setGeoPending(false);
        }, 0);
      },
    );
    return () => {
      release();
      if (sortResetHandle !== undefined) {
        window.clearTimeout(sortResetHandle);
      }
    };
  }, [apiBaseUrl, orgCode, query, session, sort, t, userPosition]);

  // Warm geolocation (same decision tree as Shops) so distanceAsc restore / meta work.
  useEffect(() => {
    if (userPosition !== null) {
      return;
    }
    if (typeof navigator === 'undefined') {
      return;
    }

    let cancelled = false;
    let releaseAcquire: (() => void) | undefined;

    const warmAcquire = (): void => {
      if (cancelled || releaseAcquire !== undefined) {
        return;
      }
      releaseAcquire = acquireBrowserPosition(
        (position) => {
          if (cancelled) {
            return;
          }
          writeDirectoryGeoAllowedFlag(true);
          setUserPosition(position);
          setGeoPending(false);
        },
        (denied) => {
          if (denied) {
            writeDirectoryGeoAllowedFlag(false);
          }
        },
      );
    };

    const applyWarmDecision = (permission: DirectoryGeoPermissionProbe): void => {
      if (cancelled) {
        return;
      }
      const action = decideDirectoryGeoWarm(permission, readDirectoryGeoAllowedFlag());
      if (action === 'warm') {
        warmAcquire();
        return;
      }
      if (action === 'clearFlagAndSkip') {
        writeDirectoryGeoAllowedFlag(false);
      }
    };

    const permissionsQuery = navigator.permissions?.query;
    if (typeof permissionsQuery === 'function') {
      void permissionsQuery
        .call(navigator.permissions, { name: 'geolocation' })
        .then((status) => {
          if (
            status.state === 'granted' ||
            status.state === 'prompt' ||
            status.state === 'denied'
          ) {
            applyWarmDecision(status.state);
            return;
          }
          applyWarmDecision('unavailable');
        })
        .catch(() => {
          applyWarmDecision('queryFailed');
        });
    } else {
      applyWarmDecision('unavailable');
    }

    return () => {
      cancelled = true;
      releaseAcquire?.();
    };
  }, [userPosition]);

  // Same unscoped Obchody directory as `/shops` — never tenant-discovery fail-open.
  // G7: shared key under public sales-points prefix (invalidates with `/shops`);
  // catalog-parity staleTime — keep last allowlist visible while background refetching
  // (blanking on every remount caused a double skeleton flash on Favorites).
  // Fail closed only until the first successful allowlist; refetchInterval mirrors `/shops`.
  // G8 — allowlist drain already walks full public SP pages; merge logo/rim into
  // pending LS so `tenant_branding_updated` (shops SSE invalidate) + F5 do not
  // leave forever-stale staged branding.
  const listableDirectoryQuery = useQuery({
    queryKey: customerQueryKeys.publicCustomerSalesPointListableAllowlist(apiBaseUrl),
    queryFn: async ({ signal }) => {
      const brandingBuffer: PublicCustomerSalesPointDTO[] = [];
      const allowlist = await fetchPublicCustomerSalesPointListableAllowlist(apiBaseUrl, {
        signal,
        onPageItems: (items) => {
          brandingBuffer.push(...items);
        },
      });
      mergePendingFavoriteBrandingFromLive(brandingBuffer);
      return {
        allowedIds: new Set(allowlist.salesPointIds),
        allowedSlugKeys: new Set(allowlist.tenantSlugKeys),
      };
    },
    enabled: localItems.length > 0,
    ...shopsDirectoryQueryOptions(directorySseConnected),
  });

  const allowlistReadyForDisplay =
    listableDirectoryQuery.isSuccess && listableDirectoryQuery.data !== undefined;

  const items = useMemo(() => {
    // Fail closed: enterable rows only after a successful public directory allowlist.
    if (!allowlistReadyForDisplay || listableDirectoryQuery.data === undefined) {
      return [];
    }
    return filterPendingFavoritesByListableDirectory(
      localItems,
      listableDirectoryQuery.data.allowedIds,
      listableDirectoryQuery.data.allowedSlugKeys,
    );
  }, [allowlistReadyForDisplay, listableDirectoryQuery.data, localItems]);

  const reconcilingListable =
    localItems.length > 0 && listableDirectoryQuery.isPending;

  const favoriteIdSet = useMemo(() => {
    const ids = new Set<number>();
    for (const item of items) {
      if (typeof item.salesPointId === 'number' && item.salesPointId > 0) {
        ids.add(item.salesPointId);
      }
    }
    return ids;
  }, [items]);

  // Public directory pages for map pins + distanceAsc lat/lng enrichment.
  // Same queryKey family as `/shops` (platform, default API sort).
  const needsPublicDirectoryPages =
    items.length > 0 && (viewMode === 'map' || sort === 'distanceAsc');

  const publicDirectoryQuery = useInfiniteQuery({
    queryKey: customerQueryKeys.publicCustomerSalesPoints(apiBaseUrl, null, null),
    queryFn: ({ pageParam, signal }) =>
      fetchPublicCustomerSalesPoints(apiBaseUrl, {
        cursor: pageParam,
        tenantCode: null,
        sort: null,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: needsPublicDirectoryPages,
    ...shopsDirectoryQueryOptions(directorySseConnected),
  });

  const hasNextPage = publicDirectoryQuery.hasNextPage;
  const isFetchingNextPage = publicDirectoryQuery.isFetchingNextPage;
  const fetchNextPage = publicDirectoryQuery.fetchNextPage;

  // Distance sort needs full directory for coords — drain while active + geo ready.
  useEffect(() => {
    if (sort !== 'distanceAsc' || userPosition === null || !needsPublicDirectoryPages) {
      return;
    }
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }
    void fetchNextPage();
  }, [
    sort,
    userPosition,
    needsPublicDirectoryPages,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  // Map mode needs full directory for favorite markers — drain while active.
  useEffect(() => {
    if (viewMode !== 'map' || !needsPublicDirectoryPages) {
      return;
    }
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }
    void fetchNextPage();
  }, [viewMode, needsPublicDirectoryPages, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const publicDirectoryItems = useMemo(
    () => publicDirectoryQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [publicDirectoryQuery.data?.pages],
  );

  // G8 — map / distanceAsc path may hold fresher SP DTOs after branding SSE;
  // merge into pending LS when infinite pages update (allowlist may still be staleTime-warm).
  useEffect(() => {
    if (publicDirectoryItems.length === 0) {
      return;
    }
    mergePendingFavoriteBrandingFromLive(publicDirectoryItems);
  }, [publicDirectoryItems]);

  const coordsBySalesPointId = useMemo(() => {
    const map = new Map<number, { lat: number | null; lng: number | null }>();
    for (const sp of publicDirectoryItems) {
      map.set(sp.salesPointId, { lat: sp.lat, lng: sp.lng });
    }
    return map;
  }, [publicDirectoryItems]);

  const filterableItems = useMemo((): FavoriteFilterItem[] => {
    return items.map((item) => {
      const id = item.salesPointId;
      const coords =
        typeof id === 'number' && id > 0 ? coordsBySalesPointId.get(id) : undefined;
      return {
        ...item,
        name: resolveLocalizedName(item.name, item.nameLocales, i18n.language),
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      };
    });
  }, [coordsBySalesPointId, i18n.language, items]);

  const orgFacets = useMemo(() => buildOrgFacets(items), [items]);

  const visibleItems = useMemo(
    () =>
      filterDirectoryShops(filterableItems, {
        query,
        sort,
        orgCode,
        userLat: userPosition?.lat,
        userLng: userPosition?.lng,
      }),
    [filterableItems, orgCode, query, sort, userPosition],
  );

  const favoritePublicItems = useMemo((): PublicCustomerSalesPointDTO[] => {
    if (favoriteIdSet.size === 0) {
      return [];
    }
    return publicDirectoryItems.filter((sp) => favoriteIdSet.has(sp.salesPointId));
  }, [favoriteIdSet, publicDirectoryItems]);

  const mapVisibleItems = useMemo(
    () =>
      filterDirectoryShops(favoritePublicItems, {
        query,
        sort,
        orgCode,
        userLat: userPosition?.lat,
        userLng: userPosition?.lng,
      }),
    [favoritePublicItems, orgCode, query, sort, userPosition],
  );

  const mapReady =
    viewMode === 'map' &&
    !publicDirectoryQuery.isPending &&
    hasNextPage !== true &&
    isFetchingNextPage !== true;

  const filtersActive =
    query.trim().length > 0 || orgCode !== null || sort !== 'nameAsc';

  const [selectionFilterKey, setSelectionFilterKey] = useState(
    () => `${query}\0${orgCode ?? ''}\0${sort}`,
  );
  const nextSelectionFilterKey = `${query}\0${orgCode ?? ''}\0${sort}`;
  if (selectionFilterKey !== nextSelectionFilterKey) {
    setSelectionFilterKey(nextSelectionFilterKey);
    setSelectedSalesPointId(null);
  }

  const handleRequestMyLocation = (): void => {
    acquireBrowserPosition(
      (position) => {
        writeDirectoryGeoAllowedFlag(true);
        setUserPosition(position);
      },
      (denied) => {
        if (denied) {
          writeDirectoryGeoAllowedFlag(false);
        }
        showToast({
          message: t(
            denied
              ? 'directory.filters.locationDenied'
              : 'directory.filters.locationUnavailable',
          ),
          variant: 'warn',
        });
      },
    );
  };

  const [unfavoriteTarget, setUnfavoriteTarget] = useState<PendingFavoriteSpContact | null>(
    null,
  );

  const title = t('account.shop.favoritesTitle');
  const description = t('account.shop.favoritesDescription');
  const showChromeControls = items.length > 0;
  const mapLayoutActive =
    viewMode === 'map' && items.length > 0 && mapVisibleItems.length > 0;
  const compactMapChrome = mapLayoutActive && shortLandscape;

  const pageHeader = (
    <DirectoryPageHeader
      title={title}
      description={description}
      titleAs="h1"
      testId="customer-guest-favorites-header"
    />
  );

  const unfavoriteDialog = (
    <ConfirmDialog
      open={unfavoriteTarget !== null}
      title={t('account.shop.unfavoriteConfirmTitle')}
      description={t('account.shop.unfavoriteConfirmBody', {
        name: unfavoriteTarget
          ? resolveLocalizedName(
              unfavoriteTarget.name,
              unfavoriteTarget.nameLocales,
              i18n.language,
            )
          : '',
      })}
      confirmLabel={t('account.shop.unfavorite')}
      cancelLabel={t('shared.cancel')}
      confirmIntent="danger"
      testId="customer-guest-favorites-unfavorite-confirm"
      onCancel={() => {
        setUnfavoriteTarget(null);
      }}
      onConfirm={() => {
        if (unfavoriteTarget?.salesPointId !== undefined) {
          removePendingFavoriteSpContact(unfavoriteTarget.salesPointId);
        }
        setUnfavoriteTarget(null);
      }}
    />
  );

  if (awaitingCredentialsHydrate) {
    return (
      <div
        className={DIRECTORY_PAGE_STACK_CLASS}
        data-testid="customer-guest-favorites-hydrating"
      >
        {pageHeader}
        <DirectoryResultsSkeleton
          density={density}
          listLayout="split2"
          size="lg"
          rowDensity="comfortable"
          testId="customer-guest-favorites-skeleton-group"
        />
      </div>
    );
  }

  if (signedIn) {
    return <AccountShopContactsMachine />;
  }

  if (localItems.length === 0 || (items.length === 0 && !reconcilingListable)) {
    return (
      <div
        className={DIRECTORY_PAGE_STACK_CLASS}
        data-testid="customer-guest-favorites-empty"
      >
        {pageHeader}
        {/* Empty card first; guest sign-in banner below (same order as list view). */}
        <div
          className="grid w-full min-w-0 grid-cols-1 gap-3"
          data-testid="customer-guest-favorites-empty-panels"
        >
          <Card
            padded
            className={DIRECTORY_EMPTY_PANEL_FULL_CLASS}
            data-testid="customer-guest-favorites-empty-card"
          >
            <p className="text-sm text-[var(--color-on-surface-muted)]">
              {t('account.shop.favoritesEmpty')}
            </p>
            <p className="mt-3 text-sm text-[var(--color-on-surface)]">
              {t('account.shop.guestFavoritesEmptyHint')}
            </p>
            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              {showBrowseNav ? (
                <Link
                  className="text-[var(--color-accent)] underline"
                  to={PLATFORM_BROWSE_PATH}
                  data-testid="customer-guest-favorites-go-browse"
                >
                  {t('nav.browse')}
                </Link>
              ) : null}
              <Link
                className="text-[var(--color-accent)] underline"
                to={PLATFORM_SHOPS_PATH}
                data-testid="customer-guest-favorites-go-shops"
              >
                {t('nav.shops')}
              </Link>
              <Link
                className="text-[var(--color-accent)] underline"
                to={PLATFORM_ORGS_PATH}
                data-testid="customer-guest-favorites-go-orgs"
              >
                {t('nav.orgs')}
              </Link>
            </div>
          </Card>
          {showSignInBanner ? (
            <GuestFavoritesSignInBanner onHide={hideSignInBanner} density="empty" />
          ) : null}
        </div>
      </div>
    );
  }

  if (reconcilingListable) {
    return (
      <div
        className={DIRECTORY_PAGE_STACK_CLASS}
        data-testid="customer-guest-favorites-reconciling"
      >
        {pageHeader}
        <DirectoryResultsSkeleton
          density={density}
          listLayout="split2"
          size="lg"
          rowDensity="comfortable"
          testId="customer-guest-favorites-skeleton-group"
        />
        {showSignInBanner ? (
          <GuestFavoritesSignInBanner onHide={hideSignInBanner} density={density} />
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={[
        DIRECTORY_PAGE_STACK_CLASS,
        mapLayoutActive ? 'min-h-0 flex-1' : '',
        compactMapChrome ? 'gap-2 pt-0' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="customer-guest-favorites-list"
      data-directory-map-layout={mapLayoutActive ? 'active' : undefined}
    >
      {compactMapChrome ? null : pageHeader}

      {showChromeControls ? (
        <DirectoryListMapViewSwitch
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          listLabel={t('directory.mapView.list')}
          mapLabel={t('directory.mapView.map')}
          groupAriaLabel={t('directory.mapView.viewSwitchAria')}
          listAriaLabel={t('directory.mapView.listAria')}
          mapAriaLabel={t('directory.mapView.mapAria')}
          testIdPrefix="customer-guest-favorites"
        />
      ) : null}

      {showChromeControls && !compactMapChrome ? (
        <div className="shrink-0">
          <DirectoryFindToolbar
            query={query}
            onQueryChange={setQuery}
            sort={sort}
            onSortChange={handleSortChange}
            orgFacets={orgFacets}
            orgCode={orgCode}
            onOrgCodeChange={setOrgCode}
            sortOptions={FAVORITES_DIRECTORY_SORT_OPTIONS}
            searchPlaceholder={t('directory.filters.searchFavoritesShort')}
            testIdPrefix="customer-guest-favorites-filters"
            densityMode={density}
            onDensityChange={setDensity}
            showDensityToggle={showDensityToggle ? viewMode === 'list' : false}
            densityTestId="customer-guest-favorites-density"
            toolbarTestId="customer-guest-favorites-find-toolbar"
          />
        </div>
      ) : null}

      {showChromeControls &&
      ((viewMode === 'list' && visibleItems.length === 0) ||
        (viewMode === 'map' && mapReady && mapVisibleItems.length === 0)) ? (
        <Card padded>
          <p
            className="text-sm text-[var(--color-on-surface-muted)]"
            data-testid="customer-guest-favorites-no-matches"
          >
            {t('directory.filters.noMatches')}
          </p>
          {filtersActive ? (
            <Button
              type="button"
              intent="secondary"
              block
              className="mt-4"
              data-testid="customer-guest-favorites-clear-filters"
              onClick={() => {
                setQuery('');
                setOrgCode(null);
                // G5 — clear-all must go through handleSortChange (remote sync when signed in).
                handleSortChange('nameAsc');
              }}
            >
              {t('directory.filters.clearAll')}
            </Button>
          ) : null}
        </Card>
      ) : null}

      {viewMode === 'map' && items.length > 0 && mapVisibleItems.length > 0 ? (
        <div
          className={`${CUSTOMER_CONTENT_FULL_CLASS} flex min-h-0 min-w-0 flex-1 flex-col`}
        >
          <ShopsDirectoryMap
            visibleItems={mapVisibleItems}
            userPosition={userPosition}
            selectedSalesPointId={selectedSalesPointId}
            mapReady={mapReady}
            onSelectSalesPointId={setSelectedSalesPointId}
            onRequestMyLocation={handleRequestMyLocation}
            persistTenant={PLATFORM_DIRECTORY_SHOPS_TENANT}
            showTenantChipOnSelectionCard={false}
            onBackToList={() => {
              setViewMode('list');
            }}
            savedViews={savedViews}
            savedViewsLoading={savedViewsLoading}
            savedViewsError={savedViewsError}
            onRetrySavedViews={onRetrySavedViews}
            onRequestDeleteView={onRequestDeleteView}
            onRequestRenameView={onRequestRenameView}
            deletePending={deletePending}
            renamePending={renamePending}
          />
        </div>
      ) : null}

      {viewMode === 'list' && visibleItems.length > 0 ? (
        <DirectoryResults
          density={density}
          listLayout="split2"
          cardsLayout="dense"
          testId="customer-guest-favorites-group"
        >
          {visibleItems.map((item) => {
            const id = item.salesPointId ?? item.salesPointSlug ?? item.code;
            return (
              <li
                key={`${item.tenantCode}-${id}`}
                className={favoritesResultItemClass(density)}
                {...(density === 'cards' ? { 'data-density': 'cards' as const } : {})}
              >
                <GuestFavoriteRow
                  item={item}
                  layout={density === 'cards' ? 'stack' : 'inline'}
                  allowlistReady={allowlistReadyForDisplay}
                  allowlistData={listableDirectoryQuery.data}
                  onRequestUnfavorite={() => {
                    setUnfavoriteTarget(item);
                  }}
                />
              </li>
            );
          })}
        </DirectoryResults>
      ) : null}

      {showSignInBanner && viewMode === 'list' ? (
        <GuestFavoritesSignInBanner onHide={hideSignInBanner} density={density} />
      ) : null}
      {unfavoriteDialog}
    </div>
  );
}

/** Soft loading stub while lazy chunk loads — mirrors signed-in skeleton. */
export function GuestFavoritesScreenFallback(): JSX.Element {
  const { density } = useDirectoryDensity(DIRECTORY_DENSITY_STORAGE_KEYS.favorites);
  const { t } = useTranslation('customer');
  return (
    <div className={DIRECTORY_PAGE_STACK_CLASS}>
      <DirectoryPageHeader
        title={t('account.shop.favoritesTitle')}
        description={t('account.shop.favoritesDescription')}
        titleAs="h1"
        testId="customer-guest-favorites-header"
      />
      <DirectoryResultsSkeleton
        density={density}
        listLayout="split2"
        size="lg"
        rowDensity="comfortable"
        testId="customer-guest-favorites-skeleton-group"
      />
    </div>
  );
}
